/**
 * Tooltip — Radix `Tooltip` themed to the radical chrome.
 * Inverse pip on `bg-ink` with paper text, mono uppercase, sharp corners,
 * 5 px arrow. The `TooltipProvider` is mounted once at the app root; consumer
 * code pairs `<Tooltip content="…">` with any focusable trigger.
 *
 * Extrapolated within the design language — no dedicated artboard.
 */

import * as RadixTooltip from '@radix-ui/react-tooltip';
import type { ReactNode } from 'react';

interface TooltipProviderProps {
  children: ReactNode;
  /** Delay in ms before the first tooltip in a hover-chain shows. */
  delayDuration?: number;
}

export function TooltipProvider({ children, delayDuration = 250 }: TooltipProviderProps) {
  return (
    <RadixTooltip.Provider delayDuration={delayDuration} skipDelayDuration={150}>
      {children}
    </RadixTooltip.Provider>
  );
}

interface TooltipProps {
  /** The visible label. */
  content: ReactNode;
  children: ReactNode;
  side?: 'top' | 'right' | 'bottom' | 'left';
  align?: 'start' | 'center' | 'end';
  /** Pass `false` to render the trigger only, e.g. when content is empty. */
  enabled?: boolean;
  /** Render the trigger inline rather than wrapping in a button. */
  asChild?: boolean;
  className?: string;
}

export function Tooltip({
  content,
  children,
  side = 'top',
  align = 'center',
  enabled = true,
  asChild = true,
  className = '',
}: TooltipProps) {
  if (!enabled || content == null || content === '') {
    return <>{children}</>;
  }
  return (
    <RadixTooltip.Root>
      <RadixTooltip.Trigger asChild={asChild}>{children}</RadixTooltip.Trigger>
      <RadixTooltip.Portal>
        <RadixTooltip.Content
          side={side}
          align={align}
          sideOffset={6}
          className={
            'z-50 max-w-[260px] border border-ink bg-ink px-2 py-1.5 font-mono text-[10.5px] uppercase leading-tight tracking-[0.06em] text-paper shadow-[0_8px_20px_rgba(0,0,0,0.25)] ' +
            'data-[state=delayed-open]:animate-in data-[state=delayed-open]:fade-in-0 data-[state=delayed-open]:zoom-in-95 ' +
            'data-[state=closed]:animate-out data-[state=closed]:fade-out-0 ' +
            className
          }
        >
          {content}
          <RadixTooltip.Arrow className="fill-ink" width={10} height={5} />
        </RadixTooltip.Content>
      </RadixTooltip.Portal>
    </RadixTooltip.Root>
  );
}
